import { useState, useEffect, useRef } from "react";
import { CustomerForm } from "./components/CustomerForm";
import { ConfigPage } from "./components/ConfigPage";
import { getConfig } from "./lib/config";
import { pingDesktopPeer } from "./lib/trpc-client";
import "./index.css";

export function App() {
  const [showConfig, setShowConfig] = useState(false);
  const [titleClickCount, setTitleClickCount] = useState(0);
  const [pageTitle, setPageTitle] = useState("顾客信息填写");
  const [isOnline, setIsOnline] = useState(false);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 启动持续 ping 循环
  const startPingLoop = (peerId: string) => {
    // 清理旧的定时器
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    // 立即测试一次连接
    const checkConnection = async () => {
      if (peerId) {
        try {
          const isConnected = await pingDesktopPeer(peerId);
          setIsOnline(isConnected);
        } catch {
          setIsOnline(false);
        }
      } else {
        setIsOnline(false);
      }
    };

    checkConnection();

    // 设置持续 ping（每 5 秒 ping 一次）
    pingIntervalRef.current = setInterval(() => {
      checkConnection();
    }, 5000);
  };

  // 停止 ping 循环
  const stopPingLoop = () => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  };

  useEffect(() => {
    // 加载配置
    const config = getConfig();
    setPageTitle(config.pageTitle || "顾客信息填写");

    // 启动持续 ping
    if (config.desktopPeerId) {
      startPingLoop(config.desktopPeerId);
    } else {
      setIsOnline(false);
    }

    // 清理函数
    return () => {
      stopPingLoop();
    };
  }, []);

  useEffect(() => {
    // 点击计数重置定时器（2秒内未点击则重置）
    if (titleClickCount > 0) {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
      clickTimeoutRef.current = setTimeout(() => {
        setTitleClickCount(0);
      }, 2000);
    }

    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, [titleClickCount]);

  const handleTitleClick = () => {
    const newCount = titleClickCount + 1;
    setTitleClickCount(newCount);

    if (newCount >= 6) {
      setShowConfig(true);
      setTitleClickCount(0);
    }
  };

  const handleConfigBack = () => {
    setShowConfig(false);
    // 重新加载配置
    const config = getConfig();
    setPageTitle(config.pageTitle || "顾客信息填写");

    // 重新启动持续 ping
    if (config.desktopPeerId) {
      startPingLoop(config.desktopPeerId);
    } else {
      stopPingLoop();
      setIsOnline(false);
    }
  };

  if (showConfig) {
    return <ConfigPage onBack={handleConfigBack} />;
  }

  // 在开发环境下，__APP_VERSION__ 可能未被构建工具注入，这里做一次安全的回退
  const version =
    typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev";

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <h1
            onClick={handleTitleClick}
            className="text-2xl font-bold text-center cursor-pointer select-none"
          >
            {pageTitle}
          </h1>
          <div className="text-center mt-2">
            {isOnline ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                连接正常
              </span>
            ) : (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                连接异常，请检查配置文件或服务器状态
              </span>
            )}
          </div>
        </div>
      </header>
      <main className="pb-20">
        <CustomerForm pageTitle={pageTitle} isOnline={isOnline} />
        <footer className="text-center text-xs text-muted-foreground">
          <span>版本：{version}</span>
        </footer>
      </main>
    </div>
  );
}

export default App;
